import supabase from "@/services/supabaseClient.js";

const STAGING_ACCESS_CACHE_KEY = "afrimed:staging:access:v1";
const STAGING_ACCESS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const SELF_SERVICE_STAGING_JOURNEYS = new Set(["patient", "clinician", "research"]);

export function normalizeStagingEmail(value) {
  return String(value || "").trim().toLowerCase();
}

export function normalizeStagingJourney(value) {
  const journey = String(value || "").trim().toLowerCase();
  return SELF_SERVICE_STAGING_JOURNEYS.has(journey) ? journey : "";
}

export function isLocalRuntimeHost(hostname = "") {
  const host = String(hostname || "").trim().toLowerCase();
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "0.0.0.0" ||
    host === "::1" ||
    host.endsWith(".local")
  );
}

export function isStagingRuntimeHost(hostname = "") {
  const host = String(hostname || "").trim().toLowerCase();
  return (
    host === "staging.afrimed.tech" ||
    host.endsWith("--afrimed-staging.netlify.app")
  );
}

export function shouldEnforceStagingAccess(hostname = "") {
  // This is a runtime boundary, not an optional build-time feature. A missing
  // Vite variable must never turn the canonical staging host into open login
  // mode, because allowlisted new identities then cannot be created.
  return isStagingRuntimeHost(hostname) && !isLocalRuntimeHost(hostname);
}

export function getRuntimeHostname() {
  if (typeof window === "undefined") return "";
  return String(window.location.hostname || "").trim().toLowerCase();
}

export function readCachedStagingAccess() {
  try {
    const raw = localStorage.getItem(STAGING_ACCESS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const email = normalizeStagingEmail(parsed?.email);
    const grantedAt = Number(parsed?.grantedAt || 0);
    if (!email || !grantedAt) return null;
    if (Date.now() - grantedAt > STAGING_ACCESS_CACHE_TTL_MS) return null;
    return {
      email,
      grantedAt,
    };
  } catch {
    return null;
  }
}

export function writeCachedStagingAccess(email = "") {
  const normalizedEmail = normalizeStagingEmail(email);
  if (!normalizedEmail) return;
  try {
    localStorage.setItem(
      STAGING_ACCESS_CACHE_KEY,
      JSON.stringify({
        email: normalizedEmail,
        grantedAt: Date.now(),
      }),
    );
  } catch {
    // ignore storage failures
  }
}

export function clearCachedStagingAccess() {
  try {
    localStorage.removeItem(STAGING_ACCESS_CACHE_KEY);
  } catch {
    // ignore storage failures
  }
}

export async function checkStagingAccess(email = "") {
  const normalizedEmail = normalizeStagingEmail(email);
  if (!normalizedEmail) return { allowed: false, email: "" };

  const { data, error } = await supabase.rpc("check_staging_access", {
    target_email: normalizedEmail,
  });

  if (error) throw error;

  return {
    allowed: data === true,
    email: normalizedEmail,
  };
}

export async function requestStagingAccess({
  email = "",
  journey = "",
  path = "/signup",
  context = {},
} = {}) {
  const normalizedEmail = normalizeStagingEmail(email);
  if (!normalizedEmail) {
    throw new Error("Enter the email address you would like to use.");
  }
  const normalizedJourney = normalizeStagingJourney(journey);
  if (!normalizedJourney) {
    throw new Error("Choose the Patient, Clinician, or Research • Public health journey.");
  }

  // The browser can submit only a narrow intended journey. The server creates
  // and owns the opaque access context and its corresponding governed lane.
  const { data, error } = await supabase.functions.invoke(
    "request-private-staging-access",
    {
      body: {
        email: normalizedEmail,
        journey: normalizedJourney,
        path: String(path || "").trim() || null,
        context: context && typeof context === "object" ? context : {},
      },
    },
  );

  if (error) throw error;
  if (data?.ok === false) {
    const requestError = new Error(
      String(data?.message || "AfriMed could not send a secure access email right now."),
    );
    requestError.code = String(data?.error || "staging_access_request_failed");
    throw requestError;
  }
  return data || { ok: true, allowed: false };
}

export async function resolveStagingFirstUseDisposition() {
  const { data, error } = await supabase.functions.invoke("resolve-staging-first-use", {
    body: {},
  });
  if (error) throw error;
  return {
    controlled: data?.controlled === true,
    journey: String(data?.journey || "").trim(),
    role: String(data?.role || "").trim(),
    stage: String(data?.stage || "").trim(),
    onboardingCompleted: data?.onboarding_completed === true,
    researchContext:
      data?.research_context && typeof data.research_context === "object"
        ? data.research_context
        : null,
    institutionMembership:
      data?.institution_membership && typeof data.institution_membership === "object"
        ? data.institution_membership
        : { assigned: false, destination: "" },
  };
}

export async function advanceStagingFirstUse(nextStage = "") {
  const { data, error } = await supabase.functions.invoke("resolve-staging-first-use", {
    body: { action: "advance", next_stage: String(nextStage || "").trim() },
  });
  if (error) throw error;
  return data || {};
}

export async function selectSelfServiceFirstUseCategory(category = "") {
  const { data, error } = await supabase.functions.invoke("resolve-staging-first-use", {
    body: { action: "select_category", category: String(category || "").trim() },
  });
  if (error) throw error;
  if (data?.controlled !== true) {
    throw new Error(String(data?.error || "first_use_category_unavailable"));
  }
  return data || {};
}

export async function saveResearchFirstUseStep({ stage = "", data = {} } = {}) {
  const { data: response, error } = await supabase.functions.invoke(
    "resolve-staging-first-use",
    {
      body: {
        action: "save_research_step",
        stage: String(stage || "").trim(),
        data: data && typeof data === "object" ? data : {},
      },
    },
  );
  if (error) throw error;
  if (response?.error) {
    const requestError = new Error(String(response.error));
    requestError.code = String(response.error);
    throw requestError;
  }
  return response || {};
}

export async function moveResearchFirstUseBack() {
  const { data, error } = await supabase.functions.invoke("resolve-staging-first-use", {
    body: { action: "back_research_step" },
  });
  if (error) throw error;
  if (data?.error) {
    const requestError = new Error(String(data.error));
    requestError.code = String(data.error);
    throw requestError;
  }
  return data || {};
}

export async function updateResearchPublicHealthContext(data = {}) {
  const { data: response, error } = await supabase.functions.invoke(
    "resolve-staging-first-use",
    {
      body: {
        action: "update_research_context",
        data: data && typeof data === "object" ? data : {},
      },
    },
  );
  if (error) throw error;
  if (response?.error) {
    const requestError = new Error(String(response.error));
    requestError.code = String(response.error);
    throw requestError;
  }
  return response || {};
}
